/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type VehicleSummary } from '@/types';

/**
 * Extracts only ID-related properties from vehicleMap, excluding specified properties
 * @param vehicleQuestionsIds From vehicle map in the header response
 * @param asPipeSeparatedString When true, returns ID properties as a pipe-separated string
 * @returns Object containing only the ID properties from the vehicle map, null if no ID properties exist,
 * or a pipe-separated string if asPipeSeparatedString is true
 */
export const extractVehicleQuestions = (
  vehicleMap?: Record<string, string | number> | VehicleSummary,
  asPipeSeparatedString = false
): Record<string, string | number> | string => {
  if (!vehicleMap) {
    return asPipeSeparatedString ? '' : {};
  }

  // Properties to explicitly exclude, even if they contain "Id"
  const excludedProperties = [
    'acesVehicleId',
    'partsProVehicleId',
    'vehicleTypeId',
    'catalogVehicleId',
    'vehicleId',
    'makeId',
    'modelId',
  ];

  const idProperties: Record<string, string | number> = {};

  Object.entries(vehicleMap).forEach(([key, value]) => {
    if ((key.includes('Id') || key.includes('ID')) && !excludedProperties.includes(key)) {
      if (typeof value === 'string' || typeof value === 'number') {
        idProperties[key] = value;
      }
    }
  });

  // If there are no ID properties, return empty string instead of empty object
  if (Object.keys(idProperties).length === 0) {
    return '';
  }

  if (asPipeSeparatedString) {
    return Object.entries(idProperties)
      .map(([key, value]) => `${key}:${value}`)
      .join('||');
  }

  return idProperties;
};
